import React from "react";
import { RegExpParser } from "regexpp";
import Select from "@cloudscape-design/components/select";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import FormField from "@cloudscape-design/components/form-field";
import Toggle from "@cloudscape-design/components/toggle";
import Checkbox from "@cloudscape-design/components/checkbox";
import RadioGroup from "@cloudscape-design/components/radio-group";
import Multiselect from "@cloudscape-design/components/multiselect";
import Alert from "@cloudscape-design/components/alert";
import Textarea from "@cloudscape-design/components/textarea";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userStore } from "../stores/access";
import { walletGroupsEditModalStore } from "../stores/walletGroupsEditModal";

const getPartnerExceptionEntries = (form) => {
	if (typeof form.partnerExceptionsText === "string") return form.partnerExceptionsText.split(/[\s,]+/).filter(Boolean);
	return Array.isArray(form.partnerExceptions) ? form.partnerExceptions : [];
};

export const getStoredVerification = ({ enabled, types, currencyMode, currencies, partnerExceptions = [], partnerExceptionsText }) => {
	if (!enabled) return "0";
	const stored = ["phone", "email"].filter((type) => types.includes(type));
	if (currencyMode === "selected") stored.push({ currencies: currencies.slice().sort() });
	const entries = getPartnerExceptionEntries({ partnerExceptions, partnerExceptionsText });
	if (entries.length) {
		stored.push({ partners: entries.map((value) => (/^\d+$/.test(String(value)) ? Number(value) : String(value))) });
	}
	return JSON.stringify(stored);
};

export const getWalletGroupFormError = (form) => {
	const partnerExceptions = getPartnerExceptionEntries(form);
	if (!form.sourceValid && !form.changed) return "Replace the invalid existing configuration before saving";
	if (!form.enabled) return "";
	if (!form.types.length) return "Select at least one verification type";
	if (form.currencyMode === "selected" && !form.currencies.length) return "Select at least one currency";
	if (partnerExceptions.some((value) => !/^\d+$/.test(String(value)) || Number(value) < 1 || !Number.isSafeInteger(Number(value)))) return "Partner exceptions must contain positive partner UIDs only";
	if (getStoredVerification(form).length > 64) return "Verification configuration exceeds the database limit of 64 characters";
	return "";
};

export const buildWalletGroupPayload = (form) => ({
	id: form.id,
	enabled: form.enabled,
	types: form.enabled ? form.types : [],
	currencyMode: form.enabled ? form.currencyMode : "all",
	currencies: form.enabled && form.currencyMode === "selected" ? form.currencies : [],
	partnerExceptions: form.enabled ? getPartnerExceptionEntries(form) : [],
});

export const validateWalletPattern = (pattern) => {
	if (typeof pattern !== "string" || pattern.startsWith("/")) {
		throw new Error("Pattern must be a regular expression without enclosing slashes or flags");
	}
	if (pattern === "") return null;
	if (pattern.length > 256) throw new Error("Pattern must not exceed 256 characters");
	if (/[\r\n\0]/.test(pattern)) throw new Error("Pattern must be a single line without control characters");
	let ast;
	try {
		ast = new RegExpParser({ ecmaVersion: 2022 }).parsePattern(pattern);
	} catch (error) {
		throw new Error("Invalid regular expression");
	}
	if (ast.alternatives.some((alternative) => alternative.elements[0]?.type !== "Assertion" || alternative.elements[0].kind !== "start")) {
		throw new Error("Start every alternative with ^ to check from the beginning of the value");
	}
	// Conservative subset: no repeated groups, lookarounds or backreferences.
	// Bound the number of backtracking choices and allow only one unbounded repetition per path.
	const inspect = (node) => {
		let choices = 1;
		let unbounded = 0;
		let width = 0;
		switch (node.type) {
			case "Pattern":
			case "Group":
			case "CapturingGroup": {
				const alternatives = node.alternatives.map(inspect);
				choices = alternatives.reduce((sum, item) => sum + item.choices, 0);
				unbounded = Math.max(...alternatives.map((item) => item.unbounded));
				width = Math.max(...alternatives.map((item) => item.width));
				break;
			}
			case "Alternative":
				for (const element of node.elements) {
					const item = inspect(element);
					choices *= item.choices;
					unbounded += item.unbounded;
					width += item.width;
				}
				break;
			case "Quantifier":
				if (!["Character", "CharacterSet", "CharacterClass"].includes(node.element.type)) {
					throw new Error("Repeated groups are not supported; repeat a character or character class instead");
				}
				if (node.min > 256 || (Number.isFinite(node.max) && node.max > 256)) throw new Error("Pattern repetitions must not exceed 256 characters");
				unbounded = Number.isFinite(node.max) ? 0 : 1;
				choices = unbounded ? 1 : node.max - node.min + 1;
				width = unbounded ? node.min : node.max;
				break;
			case "Assertion":
				if (!["start", "end", "word"].includes(node.kind)) throw new Error("Lookahead and lookbehind are not supported in payment patterns");
				break;
			case "Backreference":
				throw new Error("Backreferences are not supported in payment patterns");
			case "Character":
			case "CharacterSet":
			case "CharacterClass":
				width = 1;
				break;
			default:
				throw new Error("Unsupported pattern construction");
		}
		if (unbounded > 1) throw new Error("Use at most one open-ended repetition per alternative; replace other + or * repetitions with bounded ranges");
		if (choices > 128) throw new Error("Pattern has too many alternatives or variable repetitions; simplify it");
		if (width > 256) throw new Error("Pattern character ranges must total no more than 256 characters");
		return { choices, unbounded, width };
	};
	inspect(ast);
	let regex;
	try {
		regex = new RegExp(pattern);
	} catch (error) {
		throw new Error("Invalid regular expression");
	}
	if (regex.test("")) throw new Error("Pattern must not accept an empty value");
	return regex;
};

export const buildWalletValidationRule = (form) => {
	if (form.mode === "default") return null;
	if (!["minimum", "advanced"].includes(form.mode)) throw new Error("Choose the validation mode to apply to both settings");
	const length = (value, label) => {
		if (value === "") return undefined;
		if (!/^[0-9]+$/.test(String(value)) || !Number.isSafeInteger(Number(value))) throw new Error(`${label} must be a non-negative integer`);
		return Number(value);
	};
	const minLength = length(form.minLength, "Minimum length");
	if (form.mode === "minimum") {
		if (minLength === undefined) throw new Error("Enter the minimum length");
		return minLength;
	}
	const maxLength = length(form.maxLength, "Maximum length");
	if (minLength !== undefined && maxLength !== undefined && minLength > maxLength) throw new Error("Minimum length must not exceed maximum length");
	const rule = { digitsOnly: form.digitsOnly };
	if (minLength !== undefined) rule.minLength = minLength;
	if (maxLength !== undefined) rule.maxLength = maxLength;
	if (form.pattern !== "") {
		validateWalletPattern(form.pattern);
		rule.pattern = form.pattern;
	}
	return rule;
};

export const getWalletValidationExample = (form, value) => {
	if (form.mode !== "advanced" || value === "") return { status: "idle", message: "" };
	try {
		if (typeof value !== "string" || value.length > 256) throw new Error("Test value must be a string of at most 256 characters");
		const rule = buildWalletValidationRule(form);
		if (!value.trim()) throw new Error("The rule rejects an empty value");
		if (rule.minLength !== undefined && value.length < rule.minLength) throw new Error("Test value is shorter than Minimum length");
		if (rule.maxLength !== undefined && value.length > rule.maxLength) throw new Error("Test value is longer than Maximum length");
		if (rule.digitsOnly && /[^0-9]/.test(value)) throw new Error("Test value contains non-digit characters");
		if (rule.pattern !== undefined && !new RegExp(rule.pattern).test(value)) throw new Error("Test value does not match Pattern");
		return { status: "success", message: "Test value passes all configured rules" };
	} catch (error) {
		return { status: "error", message: error.message };
	}
};

export const getWalletValidationForm = (item) => {
	const empty = { mode: "choose", minLength: "", maxLength: "", pattern: "", digitsOnly: false };
	if (!item || !item.synchronized) return empty;
	if (!item.frontend.configured) return { ...empty, mode: "default" };
	const value = item.frontend.value;
	if (typeof value === "number" || typeof value === "string") {
		const form = { ...empty, mode: "minimum", minLength: String(value).trim() };
		try {
			buildWalletValidationRule(form);
			return form;
		} catch (error) {
			return empty;
		}
	}
	if (!value || typeof value !== "object" || Array.isArray(value)) return empty;
	if (Object.keys(value).some((key) => !["minLength", "maxLength", "pattern", "digitsOnly"].includes(key))) return empty;
	if ((value.pattern !== undefined && typeof value.pattern !== "string") || (value.digitsOnly !== undefined && typeof value.digitsOnly !== "boolean")) return empty;
	if (["minLength", "maxLength"].some((key) => value[key] !== undefined && (!Number.isSafeInteger(value[key]) || value[key] < 0))) return empty;
	const form = { mode: "advanced", minLength: value.minLength === undefined ? "" : String(value.minLength), maxLength: value.maxLength === undefined ? "" : String(value.maxLength), pattern: value.pattern || "", digitsOnly: value.digitsOnly || false };
	try {
		buildWalletValidationRule(form);
		return form;
	} catch (error) {
		return empty;
	}
};

export class WalletGroupsEditModal extends React.Component {
	state = { validationItems: [], validationLoaded: false, validationError: "", validationMethod: "", validationForm: getWalletValidationForm(null), validationChanged: false, validationSaving: false, validationSaved: false, validationExample: "" };
	validationRequest = 0;

	componentDidMount() {
		if (this.props.editModal.section === "validation") this.loadValidation();
	}

	componentDidUpdate() {
		if (this.props.editModal.section === "validation" && Number(this.props.editModal.project) !== Number(this.props.project)) this.resetModal();
	}

	componentWillUnmount() {
		this.validationRequest++;
	}

	loadValidation = () => {
		const { editModal, Interface } = this.props;
		if (Number(editModal.project) !== Number(this.props.project)) return;
		const request = ++this.validationRequest;
		this.setState({ validationLoaded: false, validationError: "", validationSaved: false });
		return Interface.request("get", { class: "data", module: "walletGroups", method: "validation" }, { id: editModal.id })
			.then((response) => {
				if (request !== this.validationRequest || Number(editModal.project) !== Number(this.props.project)) return;
				const items = response.items || [];
				const selected = items.find((item) => item.method === this.state.validationMethod) || items[0];
				this.setState({ validationItems: items, validationLoaded: true, validationMethod: selected?.method || "", validationForm: getWalletValidationForm(selected), validationExample: "", validationChanged: false });
			})
			.catch((error) => {
				if (request === this.validationRequest) this.setState({ validationLoaded: true, validationError: error?.text || error?.message || String(error) });
			});
	};

	changeValidation = (fields) => {
		this.setState({ validationForm: { ...this.state.validationForm, ...fields }, validationChanged: true, validationSaved: false });
	};

	saveValidation = () => {
		if (!this.state.validationLoaded || this.state.validationError || this.state.validationSaving || Number(this.props.editModal.project) !== Number(this.props.project)) return;
		const selected = this.state.validationItems.find((item) => item.method === this.state.validationMethod);
		if (!selected || !this.state.validationChanged) return;
		let rule;
		try {
			rule = buildWalletValidationRule(this.state.validationForm);
			const example = getWalletValidationExample(this.state.validationForm, this.state.validationExample);
			if (example.status === "error") throw new Error(example.message);
		} catch (error) {
			this.props.Interface.addNoty("Error", error.message);
			return;
		}
		const request = ++this.validationRequest;
		const project = this.props.project;
		this.setState({ validationSaving: true, validationError: "", validationSaved: false });
		return this.props.Interface.request("set", { class: "data", module: "walletGroups", method: "editValidation" }, { id: this.props.editModal.id, method: selected.method, version: selected.version, rule })
			.then((response) => {
				if (request !== this.validationRequest || Number(project) !== Number(this.props.project)) return;
				this.setState({ validationSaving: false, validationChanged: false, validationSaved: true, validationItems: this.state.validationItems.map((item) => (item.method === selected.method ? response.item : item)), validationForm: getWalletValidationForm(response.item) });
			})
			.catch((error) => {
				if (request === this.validationRequest) this.setState({ validationSaving: false, validationError: error?.text || error?.message || String(error) });
			});
	};

	renderValidation = () => {
		const { editModal } = this.props;
		const { validationItems, validationLoaded, validationSaving, validationError, validationSaved, validationForm: form } = this.state;
		const selected = validationItems.find((item) => item.method === this.state.validationMethod);
		const snapshot = (item) => (item?.configured ? JSON.stringify(item.value) : "Not configured");
		let patternError = "";
		if (form.mode === "advanced") {
			try { validateWalletPattern(form.pattern); } catch (error) { patternError = error.message; }
		}
		const example = getWalletValidationExample(form, this.state.validationExample);
		let formError = "";
		try {
			buildWalletValidationRule(form);
		} catch (error) {
			formError = error.message;
		}
		return (
			<Modal
				visible={editModal.show}
				size="large"
				onDismiss={() => {
					if (!validationSaving) this.resetModal();
				}}
				header={`Withdrawal validation · ${editModal.method}`}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button disabled={validationSaving} onClick={this.resetModal}>
								Close
							</Button>
							<Button variant="primary" loading={validationSaving} disabled={!validationLoaded || validationSaving || !selected || !this.state.validationChanged || Boolean(formError) || Boolean(validationError) || example.status === "error"} onClick={this.saveValidation}>
								Save for website and server
							</Button>
						</SpaceBetween>
					</Box>
				}>
				<SpaceBetween direction="vertical" size="m">
					<Alert type="info">Rules apply to the selected payment method. Changes are saved for both the website and server. Server changes require a Core restart; the website updates after its configuration cache refreshes.</Alert>
					{validationSaved && <Alert type="success">Saved for both the website and server. Restart Core to activate the server rule.</Alert>}
					{validationError && (
						<Alert
							type="error"
							action={
								<Button disabled={validationSaving} onClick={this.loadValidation}>
									Reload
								</Button>
							}>
							{validationError}
						</Alert>
					)}
					<FormField label="Payment method">
						<Select
							loadingText="Loading payment methods"
							statusType={!validationLoaded ? "loading" : "finished"}
							disabled={!validationLoaded || validationSaving}
							selectedOption={selected ? { value: selected.method, label: selected.method } : null}
							options={validationItems.map((item) => ({ value: item.method, label: item.method }))}
							placeholder="Select payment method"
							onChange={({ detail }) => {
								const item = validationItems.find((entry) => entry.method === detail.selectedOption.value);
								this.setState({ validationMethod: item.method, validationForm: getWalletValidationForm(item), validationExample: "", validationChanged: false, validationSaved: false });
							}}
						/>
					</FormField>
					{validationLoaded && !validationError && !validationItems.length && <Box>No payment methods are linked to this group.</Box>}
					{selected && (
						<>
							{!selected.synchronized && <Alert type="warning">The website and server currently have different rules. Choose the rule that should replace both.</Alert>}
							<FormField label="Current website rule">
								<Box variant="code">{snapshot(selected.frontend)}</Box>
							</FormField>
							<FormField label="Current server rule">
								<Box variant="code">{snapshot(selected.backend)}</Box>
							</FormField>
							<FormField label="Validation mode" errorText={form.mode === "choose" ? formError : ""}>
								<RadioGroup
									value={form.mode}
									disabled={validationSaving}
									onChange={({ detail }) => this.changeValidation({ mode: detail.value })}
									items={[
										{ value: "default", label: "Use defaults", description: "Remove this method's rules from both settings. General server limits still apply." },
										{ value: "minimum", label: "Minimum length only", description: "Keep the existing card, phone or wallet formatting." },
										{ value: "advanced", label: "Advanced rules", description: "Replace automatic masks and prefixes with these rules." },
									]}
								/>
							</FormField>
							{["minimum", "advanced"].includes(form.mode) && (
								<FormField label="Minimum length">
									<Input value={form.minLength} type="number" disabled={validationSaving} onChange={({ detail }) => this.changeValidation({ minLength: detail.value })} />
								</FormField>
							)}
							{form.mode === "advanced" && (
								<>
									<FormField label="Maximum length" description="Leave blank for no method-specific maximum.">
										<Input value={form.maxLength} type="number" disabled={validationSaving} onChange={({ detail }) => this.changeValidation({ maxLength: detail.value })} />
									</FormField>
									<FormField label="Pattern" errorText={patternError} description="Optional. Start with ^; add $ to check the whole value. No / delimiters or flags. Up to 256 characters. Repeated groups, lookarounds and backreferences are not allowed. Example: ^[0-9]{11,14}$">
										<Input value={form.pattern} disabled={validationSaving} onChange={({ detail }) => this.changeValidation({ pattern: detail.value })} />
									</FormField>
									<Toggle checked={form.digitsOnly} disabled={validationSaving} onChange={({ detail }) => this.changeValidation({ digitsOnly: detail.checked })}>
										Digits only (0–9)
									</Toggle>
									<FormField label="Test value" description="Optional synthetic example, up to 256 characters. Checks all configured rules without correcting the value. The example is not saved." errorText={example.status === "error" ? example.message : ""}>
										<Input value={this.state.validationExample} autoComplete="off" disabled={validationSaving} onChange={({ detail }) => this.setState({ validationExample: detail.value })} />
									</FormField>
									{example.status === "success" && <Alert type="success">{example.message}</Alert>}
								</>
							)}
							{form.mode !== "choose" && formError && formError !== patternError && <Alert type="error">{formError}</Alert>}
						</>
					)}
				</SpaceBetween>
			</Modal>
		);
	};

	resetModal = () => {
		this.props.setEditModal({
			...this.props.editModal,
			show: false,
			section: "verification",
			project: null,
			loaded: true,
			id: 0,
			method: "",
			enabled: false,
			types: [],
			currencyMode: "all",
			currencies: [],
			partnerExceptions: [],
			partnerExceptionsText: "",
			availableCurrencies: [],
			sourceValid: true,
			sourceError: "",
			rawVerification: "0",
			changed: false,
		});
	};

	change = (fields) => {
		this.props.setEditModal({ ...this.props.editModal, ...fields, changed: true });
	};

	toggleType = (type, checked) => {
		const types = checked
			? [...new Set([...this.props.editModal.types, type])]
			: this.props.editModal.types.filter((item) => item !== type);
		this.change({ types });
	};

	save = () => {
		const { editModal, Interface } = this.props;
		const error = getWalletGroupFormError(editModal);
		if (error) {
			Interface.addNoty("Error", error);
			return;
		}

		this.props.setEditModal({ ...editModal, loaded: false });
		Interface.request("set", { class: "data", module: "walletGroups", method: "edit" }, buildWalletGroupPayload(editModal))
			.then(() => {
				Interface.addNoty("Success", "Wallet group verification was successfully updated");
				this.resetModal();
				Interface.loadWalletGroups();
			})
			.catch((requestError) => {
				Interface.addNoty("Error", requestError?.text || requestError?.message || String(requestError));
				this.props.setEditModal({ ...editModal, loaded: true });
			});
	};

	render() {
		const { editModal } = this.props;
		if (editModal.section === "validation") return this.renderValidation();
		const currencyOptions = editModal.availableCurrencies.map((code) => ({ value: code, label: code }));
		const selectedCurrencies = currencyOptions.filter((option) => editModal.currencies.includes(option.value));
		const formError = getWalletGroupFormError(editModal);

		return (
			<Modal
				visible={editModal.show}
				onDismiss={this.resetModal}
				header="Edit wallet group verification"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button onClick={this.resetModal}>Close</Button>
							<Button variant="primary" onClick={this.save} loading={!editModal.loaded} disabled={!editModal.loaded || Boolean(formError)}>
								Save
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				<SpaceBetween direction="vertical" size="m">
					{!editModal.sourceValid ? (
						<Alert type="warning" header="Invalid existing verification value">
							{editModal.sourceError}. Current value: {String(editModal.rawVerification)}. Choose a new structured configuration to replace it.
						</Alert>
					) : null}
					<FormField label="ID"><Input value={String(editModal.id)} disabled /></FormField>
					<FormField label="Method"><Input value={editModal.method} disabled /></FormField>
					<Toggle checked={editModal.enabled} onChange={({ detail }) => this.change({ enabled: detail.checked })}>
						Require verification
					</Toggle>
					{editModal.enabled ? (
						<>
							<FormField label="Verification types">
								<SpaceBetween direction="vertical" size="xs">
									<Checkbox checked={editModal.types.includes("phone")} onChange={({ detail }) => this.toggleType("phone", detail.checked)}>Phone</Checkbox>
									<Checkbox checked={editModal.types.includes("email")} onChange={({ detail }) => this.toggleType("email", detail.checked)}>Email</Checkbox>
								</SpaceBetween>
							</FormField>
							<FormField
								label="Verification partner exceptions"
								description="Users referred by these partner UIDs can deposit without Phone or Email verification."
							>
								<Textarea
									value={editModal.partnerExceptionsText}
									placeholder="12345 67890"
									onChange={({ detail }) => this.change({ partnerExceptionsText: detail.value })}
								/>
							</FormField>
							<FormField label="Currency scope">
								<RadioGroup
									value={editModal.currencyMode}
									onChange={({ detail }) => this.change({ currencyMode: detail.value, currencies: detail.value === "all" ? [] : editModal.currencies })}
									items={[
										{ value: "all", label: "All currencies" },
										{ value: "selected", label: "Selected currencies" },
									]}
								/>
							</FormField>
							{editModal.currencyMode === "selected" ? (
								<FormField label="Currencies" description={`${getStoredVerification(editModal).length}/64 characters`} errorText={formError}>
									<Multiselect
										filteringType="auto"
										selectedOptions={selectedCurrencies}
										options={currencyOptions}
										placeholder="Select currencies"
										onChange={({ detail }) => this.change({ currencies: detail.selectedOptions.map((option) => option.value) })}
									/>
								</FormField>
							) : formError ? <Alert type="error">{formError}</Alert> : null}
						</>
					) : null}
				</SpaceBetween>
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const user = useRecoilValue(userStore);
		const editModal = useRecoilValue(walletGroupsEditModalStore);
		const setEditModal = useSetRecoilState(walletGroupsEditModalStore);
		return <Component {...props} project={user.brand?.id} editModal={editModal} setEditModal={setEditModal} />;
	};
}

export default withRecoil(WalletGroupsEditModal);
